// src/pages/user/memoir/overview.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  PlusIcon,
  MenuIcon,
  CloseIcon
} from '../../../public/icons';
import { memoirService } from '../../../services/memoirService';

const MemoirOverview = () => {
  const { memoirId } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [memoir, setMemoir] = useState(null);
  const [progressItems, setProgressItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* ── Fetch the memoir on mount / when the ID changes ── */
  useEffect(() => {
    if (!memoirId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await memoirService.getMemoirById(memoirId);

        if (cancelled) return;

        if (res?.success) {
          setMemoir(res.data);
          setProgressItems(res.data.progressItems || []);
        } else {
          setError('Memoir not found');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load memoir:', err);
        setError(err.message || 'Failed to load memoir');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [memoirId]);

  /* ── Chapters list (derived from memoir.totalWeeks) ── */
  const chapters = useMemo(() => {
    if (!memoir?.totalWeeks) return [];
    return Array.from({ length: memoir.totalWeeks }, (_, i) => i + 1);
  }, [memoir]);

  /* ── Loading state ── */
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#55000C] mx-auto" />
          <p className="mt-4 text-sm text-[#6B635D]">Loading your memoir…</p>
        </div>
      </div>
    );
  }

  /* ── Error state ── */
  if (error || !memoir) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
          <h2 className="text-lg font-medium text-[#0E1628] mb-2">
            Couldn't load this memoir
          </h2>
          <p className="text-sm text-[#6B635D] mb-6">
            {error || 'The memoir you requested does not exist.'}
          </p>
          <Link
            to="/account/profile"
            className="inline-block px-6 py-3 bg-[#55000C] text-white font-medium rounded-lg hover:bg-[#3d0009] transition-colors"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  /* ── Derived fields for the UI ── */
  const totalWeeks = memoir.totalWeeks || 12;
  const completedWeeks = memoir.currentWeek || 0;
  const statusLabel = String(memoir.status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const statusBadgeClass =
    memoir.status === 'active' || memoir.status === 'processing'
      ? 'bg-[#FFF4D6] text-yellow-800'
      : memoir.status === 'completed'
      ? 'bg-[#E7F4ED] text-[#2F8F5B]'
      : memoir.status === 'cancelled'
      ? 'bg-[#F4E7E7] text-[#A82121]'
      : 'bg-[#F2F4F7] text-[#6B635D]';

  /* ── Progress text ── */
  const progressMessage =
    memoir.status === 'completed'
      ? 'Your memoir is complete. We\'re preparing it for printing.'
      : memoir.status === 'cancelled'
      ? 'This memoir order has been cancelled.'
      : `Your Memoir is in progress. Keep responding, we're shaping your story.`;

  /* ── "What's Next" text ── */
  const whatsNextText =
    memoir.status === 'completed'
      ? 'Your memoir is complete and being prepared for shipping.'
      : memoir.status === 'active'
      ? `Week ${completedWeeks + 1} questions will be sent soon. Take your time to reply when you're ready.`
      : memoir.status === 'processing'
      ? 'We\'re preparing your memoir journey. You\'ll receive your first question shortly.'
      : 'Complete payment to activate your memoir journey.';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="px-3 sm:px-4 md:px-6 mb-4 sm:mb-6">
        <div className="text-left">
          {/* Title */}
          <h1 className="text-lg sm:text-xl md:text-2xl font-medium text-[#0E1628] mb-2 break-words">
            {memoir.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-3 md:gap-4 mb-3">
            <span className="text-xs sm:text-sm text-[#6B635D] font-light">
              {memoir.type}
            </span>
            <span className="hidden sm:inline text-xs text-[#6B635D]">•</span>
            <span className="text-xs sm:text-sm text-[#6B635D] font-light">
              Week {completedWeeks} of {totalWeeks}
            </span>
            {memoir.lastResponse && (
              <>
                <span className="hidden sm:inline text-xs text-[#6B635D]">•</span>
                <span className="text-xs sm:text-sm text-[#6B635D] font-light">
                  Last response: {memoir.lastResponse}
                </span>
              </>
            )}
          </div>

          {/* Status badge */}
          <div
            className={`inline-flex px-2 sm:px-3 py-0.5 sm:py-1 ${statusBadgeClass} text-xs sm:text-sm rounded-full font-medium`}
          >
            {statusLabel}
          </div>
        </div>

        {/* Mobile chapter menu button */}
        <div className="block lg:hidden mt-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#F7F5EF] rounded-lg border border-gray-200"
          >
            <span className="text-sm font-medium text-[#0E1628]">
              Jump to Chapter
            </span>
            {mobileMenuOpen ? (
              <CloseIcon className="w-5 h-5 text-[#6B635D]" />
            ) : (
              <MenuIcon className="w-5 h-5 text-[#6B635D]" />
            )}
          </button>
        </div>

        {/* Mobile chapter dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              <Link
                to={`/user/memoir/${memoirId}`}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm text-[#55000C] bg-[#FFF4D6] font-medium border-b border-gray-100"
              >
                Overview
              </Link>
              {chapters.map((chapter) => (
                <Link
                  key={chapter}
                  to={`/user/memoir/${memoirId}/chapter/${chapter}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm text-[#6B635D] hover:bg-gray-50 border-b border-gray-100"
                >
                  Chapter {chapter}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Desktop chapter tabs */}
        <div className="hidden lg:block overflow-x-auto border-b border-gray-200 text-left mt-6 md:mt-8 pb-0.5">
          <div className="flex gap-1 min-w-max">
            <Link
              to={`/user/memoir/${memoirId}`}
              className="px-4 py-2 text-sm text-[#55000C] border-b-2 border-[#55000C] font-medium whitespace-nowrap"
            >
              Overview
            </Link>
            {chapters.slice(0, 8).map((chapter) => (
              <Link
                key={chapter}
                to={`/user/memoir/${memoirId}/chapter/${chapter}`}
                className="px-4 py-2 text-sm text-[#8A9099] hover:text-[#0E1628] whitespace-nowrap"
              >
                Chapter {chapter}
              </Link>
            ))}
            {chapters.length > 8 && (
              <span className="px-4 py-2 text-sm text-[#8A9099] whitespace-nowrap">
                ...
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-6 md:gap-8 px-3 sm:px-4 md:px-6 pb-10 sm:pb-16 md:pb-20">
        {/* Left column */}
        <div className="w-full lg:w-2/3 space-y-5 sm:space-y-6">
          {/* Overview card */}
          <div className="bg-[#F7F5EF] p-4 sm:p-5 md:p-6 rounded-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-base sm:text-lg font-medium text-[#0E1628]">
                Memoir Overview
              </h2>
              <div className="inline-flex text-xs sm:text-sm px-2 sm:px-3 py-1 bg-[#F0F1FA] rounded-full w-fit">
                <span className="text-[#6B635D] font-light">
                  Phase 1 - {memoir.phase || 'Capture'}
                </span>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="text-left">
                <label className="text-xs sm:text-sm text-[#8A9099] font-normal w-32">
                  Book Title
                </label>
                <p className="flex-1 font-normal text-[#0E1628] text-sm sm:text-base break-words">
                  {memoir.title}
                </p>
              </div>

              <div className="text-left">
                <label className="text-xs sm:text-sm text-[#8A9099] font-normal w-32">
                  Memoir Type
                </label>
                <p className="flex-1 font-normal text-[#0E1628] text-sm sm:text-base">
                  {memoir.type}
                </p>
              </div>

              <div className="text-left">
                <label className="text-xs sm:text-sm text-[#8A9099] font-normal w-32">
                  Last Question Sent
                </label>
                <p className="flex-1 font-normal text-[#0E1628] text-sm sm:text-base">
                  {memoir.lastQuestionSent || memoir.nextPrompt || 'Not yet sent'}
                </p>
              </div>

              <div className="text-left">
                <label className="text-xs sm:text-sm text-[#8A9099] font-normal w-32">
                  Next Weekly Prompt
                </label>
                <p className="flex-1 font-normal text-[#0E1628] text-sm sm:text-base">
                  {memoir.scheduledFor
                    ? `Scheduled for ${memoir.scheduledFor}`
                    : 'Awaiting schedule'}
                </p>
              </div>
            </div>
          </div>

          {/* Progress snapshot */}
          <div className="bg-[#F7F5EF] p-4 sm:p-5 md:p-6 rounded-xl">
            <h2 className="text-base text-left sm:text-lg font-medium text-[#0E1628] mb-3 sm:mb-4">
              Progress Snapshot
            </h2>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              {Array.from({ length: totalWeeks }, (_, i) => i + 1).map(
                (week, index) => (
                  <div
                    key={week}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      index < completedWeeks
                        ? 'bg-[#55000C] text-white'
                        : index === completedWeeks
                        ? 'bg-[#D9D9D9] text-[#6B635D] border-2 border-[#55000C]'
                        : 'bg-gray-200 text-[#8A9099]'
                    }`}
                  >
                    {week}
                  </div>
                )
              )}
            </div>

            <p className="text-sm sm:text-base text-left text-[#0E1628] font-medium mb-2 sm:mb-3">
              {completedWeeks} of {totalWeeks} Weeks Completed
            </p>

            <p className="text-xs text-left sm:text-sm text-[#6B635D] font-light leading-relaxed">
              {progressMessage}
            </p>
          </div>

          {/* What's next */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 sm:p-4 rounded-r-lg">
            <h3 className="font-medium text-amber-800 text-sm sm:text-base mb-1">
              What's Next
            </h3>
            <p className="text-xs sm:text-sm text-amber-700">{whatsNextText}</p>
          </div>
        </div>

        {/* Right column — timeline */}
        <div className="w-full lg:w-1/3">
          <div className="bg-[#F7F5EF] p-4 sm:p-5 md:p-6 rounded-xl sticky top-4">
            <h2 className="text-base sm:text-lg font-medium text-[#0E1628] mb-4">
              Progress Timeline
            </h2>

            {progressItems.length === 0 ? (
              <p className="text-sm text-[#6B635D]">
                No progress yet. Your timeline will appear here once the journey begins.
              </p>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {progressItems.map((item, idx) => (
                  <div key={item.week} className="relative">
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <div
                          className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full mt-1 ${
                            item.status === 'completed'
                              ? 'bg-[#55000C]'
                              : item.status === 'awaiting'
                              ? 'bg-amber-400'
                              : 'bg-gray-300'
                          }`}
                        />
                        {idx < progressItems.length - 1 && (
                          <div
                            className={`absolute top-4 left-1/2 transform -translate-x-1/2 w-0.5 h-12 sm:h-14 ${
                              item.status === 'completed'
                                ? 'bg-[#55000C]'
                                : 'bg-gray-300'
                            }`}
                          />
                        )}
                      </div>

                      <div className="flex-1 pb-6">
                        <p
                          className={`font-medium text-sm sm:text-base ${
                            item.status === 'completed'
                              ? 'text-[#1C2A46]'
                              : 'text-[#8A9099]'
                          }`}
                        >
                          Week {item.week}
                        </p>
                        <p className="text-xs sm:text-sm font-light text-[#6B635D] mt-0.5">
                          {item.status === 'completed' &&
                            `Completed${item.date ? ` • ${item.date}` : ''}`}
                          {item.status === 'awaiting' && 'Awaiting your response'}
                          {item.status === 'pending' && 'Not started yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoirOverview;